/**
 * PurposeSection.tsx
 *
 * The sphere editor's Purpose tab: loads the sphere's purpose, writes the statement and its
 * affirmation through `purposeApi`, and offers Undo on a save. A stale-revision 409 reloads the
 * latest value and leaves the user's typed text in the field.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Toast } from 'primereact/toast';
import type { ApiClient } from '@center-technologies/shared-api-clients';
import { useTranslation } from 'localization';
import { useAnalytics } from '@center-technologies/analytics';
import type { PurposeAlsoServesEntry, SpherePurpose } from 'types/purpose';
import {
  PurposeApiError,
  addSpherePurposeLink,
  affirmSpherePurpose,
  getSpherePurpose,
  removeSpherePurposeLink,
  setSpherePurposeStatement,
} from '../../../api/purposeApi';
import { usePurposeNearby } from '../../../hooks/usePurposeNearby';
import ButtonAtom from '../../atoms/Button/ButtonAtom';
import DialogWrapper from '../../molecules/DialogWrapper/DialogWrapper';
import PurposeFieldMolecule, { PurposeFieldStatus } from '../../molecules/PurposeField/PurposeFieldMolecule';
import PurposeParentDialogMolecule from '../../molecules/PurposeParentDialog/PurposeParentDialogMolecule';
import SphereSearch, { SphereOption } from '../../molecules/SphereSearch/SphereSearch';
import './PurposeSection.scss';

/** The toast body for a save/remove Undo action — hoisted so the Toast's `content` closure never redefines a component. */
const ToastUndoContent: React.FC<{ summary: string; onUndo: () => void }> = ({ summary, onUndo }) => {
  const { t } = useTranslation();
  return (
    <div className="purpose-section__toast">
      <span>{summary}</span>
      <ButtonAtom type="button" variant="text" size="small" label={t('purpose.field.undo')} onClick={onUndo} />
    </div>
  );
};

/** Toast's `content` factory for the save/remove Undo action. Returns the render function
 * `toast.current.show` calls — hoisted to module scope (not returned inline where it's used) so
 * that JSX-returning function is lexically outside `PurposeSection`, not a component definition
 * nested inside another component's render (Sonar S6478). */
const renderToastUndoContent = (summary: string, onUndo: () => void) => () => (
  <ToastUndoContent summary={summary} onUndo={onUndo} />
);

export interface PurposeSectionProps {
  graphClient: ApiClient | null;
  sphereId: string;
  sphereName?: string;
  /** Whether the caller holds `edit`; distinguishes "only the person can write this" from "you can't edit". */
  hasEditPermission: boolean;
  /** Renders read-only whatever the server allows (the editor was opened to view). */
  viewMode?: boolean;
  /** Enables the org-wide search in the parent and link pickers. */
  organizationId?: string;
  /** Called after any successful write so the header's purpose line can refresh. */
  onPurposeChanged?: () => void;
}

const PurposeSection: React.FC<PurposeSectionProps> = ({
  graphClient,
  sphereId,
  sphereName,
  hasEditPermission,
  viewMode = false,
  organizationId,
  onPurposeChanged,
}) => {
  const { t } = useTranslation();
  const logAnalyticsEvent = useAnalytics();
  const [parentDialogOpen, setParentDialogOpen] = useState(false);
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const toast = useRef<Toast>(null);
  const [status, setStatus] = useState<PurposeFieldStatus>('loading');
  const [purpose, setPurpose] = useState<SpherePurpose | null>(null);
  const [saving, setSaving] = useState(false);
  const [affirming, setAffirming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [conflict, setConflict] = useState(false);
  const [reloads, setReloads] = useState(0);
  const revision = useRef<string | undefined>(undefined);
  const nearby = usePurposeNearby(graphClient, sphereId, parentDialogOpen);

  const adopt = useCallback((next: SpherePurpose) => {
    revision.current = next.revision;
    setPurpose(next);
  }, []);

  useEffect(() => {
    if (!graphClient || !sphereId) return undefined;
    let cancelled = false;
    setStatus('loading');
    getSpherePurpose(graphClient, sphereId)
      .then((result) => {
        if (cancelled) return;
        adopt(result);
        setStatus('ready');
      })
      .catch(() => {
        if (!cancelled) setStatus('error');
      });
    return () => {
      cancelled = true;
    };
  }, [graphClient, sphereId, reloads, adopt]);

  /** A stale-revision 409 loads the latest value; anything else is reported and the text kept. */
  const handleFailure = useCallback(
    async (err: unknown) => {
      if (err instanceof PurposeApiError && err.code === 'stale_revision' && graphClient) {
        try {
          adopt(await getSpherePurpose(graphClient, sphereId));
          setConflict(true);
          setError(null);
          return;
        } catch {
          // fall through to the generic message: the latest value could not be loaded either
        }
      }
      setError(t('purpose.errors.generic'));
    },
    [graphClient, sphereId, adopt, t]
  );

  const showUndoToast = useCallback((summary: string, undo: () => Promise<void>) => {
    toast.current?.show({
      severity: 'success',
      life: 8000,
      content: renderToastUndoContent(summary, () => {
        toast.current?.clear();
        void undo();
      }),
    });
  }, []);

  const writeStatement = useCallback(
    async (text: string | null, offerUndo: boolean, previous: string | null) => {
      if (!graphClient) return;
      setSaving(true);
      setError(null);
      try {
        const next = await setSpherePurposeStatement(graphClient, sphereId, text, revision.current);
        adopt(next);
        setConflict(false);
        logAnalyticsEvent('purpose_statement_saved', undefined, {
          sphereLabel: sphereId,
          hadStatement: previous !== null,
          cleared: text === null,
        });
        onPurposeChanged?.();
        if (offerUndo) {
          showUndoToast(text === null ? t('purpose.field.removed') : t('purpose.field.saved'), () =>
            writeStatement(previous, false, text)
          );
        }
      } catch (err) {
        await handleFailure(err);
      } finally {
        setSaving(false);
      }
    },
    [graphClient, sphereId, adopt, onPurposeChanged, showUndoToast, handleFailure, logAnalyticsEvent, t]
  );

  const handleSave = useCallback(
    (text: string | null) => writeStatement(text, true, purpose?.statement?.text ?? null),
    [writeStatement, purpose]
  );

  const handleAffirm = useCallback(async () => {
    if (!graphClient) return;
    const wasAffirmed = purpose?.statement?.stance === 'affirmed';
    setAffirming(true);
    setError(null);
    try {
      adopt(await affirmSpherePurpose(graphClient, sphereId, revision.current));
      setConflict(false);
      logAnalyticsEvent('purpose_affirmed', undefined, { sphereLabel: sphereId, unchanged: wasAffirmed });
      onPurposeChanged?.();
      toast.current?.show({ severity: 'success', life: 4000, summary: t('purpose.field.affirmed') });
    } catch (err) {
      await handleFailure(err);
    } finally {
      setAffirming(false);
    }
  }, [graphClient, sphereId, purpose, adopt, onPurposeChanged, handleFailure, logAnalyticsEvent, t]);

  const handleRemoveLink = useCallback(
    async (entry: PurposeAlsoServesEntry) => {
      if (!graphClient) return;
      try {
        adopt(await removeSpherePurposeLink(graphClient, sphereId, entry.relationshipId));
        onPurposeChanged?.();
        showUndoToast(t('purpose.field.alsoServesRemoved', { name: entry.sphere.name }), async () => {
          try {
            adopt(await addSpherePurposeLink(graphClient, sphereId, entry.sphere.id));
            onPurposeChanged?.();
          } catch (undoErr) {
            await handleFailure(undoErr);
          }
        });
      } catch (err) {
        await handleFailure(err);
      }
    },
    [graphClient, sphereId, adopt, onPurposeChanged, handleFailure, showUndoToast, t]
  );

  const handleMoved = useCallback(
    (next: SpherePurpose) => {
      adopt(next);
      setConflict(false);
      onPurposeChanged?.();
      toast.current?.show({ severity: 'success', life: 4000, summary: t('purpose.dialog.moved') });
    },
    [adopt, onPurposeChanged, t]
  );

  const handleLinkSelected = useCallback(
    async (_all: SphereOption[], one?: SphereOption | null) => {
      if (!graphClient || !one?.code) return;
      setLinkDialogOpen(false);
      try {
        adopt(await addSpherePurposeLink(graphClient, sphereId, one.code));
        onPurposeChanged?.();
        toast.current?.show({ severity: 'success', life: 4000, summary: t('purpose.field.alsoServesAdded', { name: one.name }) });
      } catch (err) {
        await handleFailure(err);
      }
    },
    [graphClient, sphereId, adopt, onPurposeChanged, handleFailure, t]
  );

  const effective: SpherePurpose | null =
    purpose && viewMode
      ? { ...purpose, capabilities: { authorStatement: false, changeParent: false, manageLinks: false } }
      : purpose;

  return (
    <>
      <Toast ref={toast} />
      <PurposeFieldMolecule
        status={status}
        purpose={effective}
        readOnlyReason={hasEditPermission && !viewMode ? 'person' : 'sphere'}
        personName={sphereName}
        saving={saving}
        affirming={affirming}
        error={error}
        conflict={conflict}
        onSave={handleSave}
        onAffirm={handleAffirm}
        onChangeParent={() => setParentDialogOpen(true)}
        onAddLink={organizationId ? () => setLinkDialogOpen(true) : undefined}
        onRemoveLink={handleRemoveLink}
        onRetry={() => setReloads((n) => n + 1)}
      />
      <PurposeParentDialogMolecule
        visible={parentDialogOpen}
        onHide={() => setParentDialogOpen(false)}
        graphClient={graphClient}
        sphereId={sphereId}
        sphereName={sphereName}
        organizationId={organizationId}
        path={purpose?.path}
        siblings={nearby.siblings}
        nearbyChildren={nearby.children}
        nearbyLoading={nearby.loading}
        revision={revision.current}
        onMoved={handleMoved}
        onStale={() => setReloads((n) => n + 1)}
      />
      {organizationId && (
        <DialogWrapper
          header={t('purpose.field.alsoServes')}
          visible={linkDialogOpen}
          onHide={() => setLinkDialogOpen(false)}
          style={{ width: 'min(32rem, 100vw)' }}
        >
          <SphereSearch
            organizationId={organizationId}
            single
            showLabel
            label={t('purpose.dialog.searchLabel')}
            placeholder={t('purpose.dialog.searchLabel')}
            onSphereSelected={handleLinkSelected}
          />
        </DialogWrapper>
      )}
    </>
  );
};

export default PurposeSection;
